import React, { useState, useEffect } from 'react';
import { logger } from '../utils/logger';
import { Instagram, Lock, Mail, Send, CheckCircle, X, Menu } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CartDrawer } from './CartDrawer';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default menu items (fallback)
const DEFAULT_MENU_ITEMS = [
  { label: 'Verhaal', url: '/verhaal', is_active: true },
  { label: 'Shop', url: '/shop', is_active: true },
  { label: 'Professionals', url: '/#professionals', is_active: true },
  { label: 'Op Maat', url: '/custom-basket', is_active: true },
  { label: 'Inspiratie', url: '/#inspiratie', is_active: true },
  { label: 'Contact', url: '/#contact', is_active: true }
];

// Desktop navigation item component
const NavItem = ({ item, scrollToSection, scrollToCasesSection }) => {
  const isHashLink = item.url.startsWith('/#');
  const sectionId = isHashLink ? item.url.replace('/#', '') : null;
  
  // Handle hash links with scroll behavior
  if (isHashLink) {
    return (
      <a 
        href={item.url}
        onClick={(e) => {
          e.preventDefault();
          if (sectionId === 'inspiratie') {
            scrollToCasesSection();
          } else {
            scrollToSection(sectionId);
          }
        }}
        className="hover:text-terra-beige transition-colors duration-200 cursor-pointer"
        style={{ color: '#6D4F47' }}
      >
        {item.label}
      </a>
    );
  }
  
  // Regular links
  return (
    <Link 
      to={item.url}
      className="hover:text-terra-beige transition-colors duration-200"
      style={{ color: '#6D4F47' }}
    >
      {item.label}
    </Link>
  );
};

// Mobile navigation item component
const MobileNavItem = ({ item, scrollToSection, scrollToCasesSection, onClose }) => {
  const isHashLink = item.url.startsWith('/#');
  const sectionId = isHashLink ? item.url.replace('/#', '') : null;
  
  // Handle hash links with scroll behavior
  if (isHashLink) {
    return (
      <a 
        href={item.url}
        onClick={(e) => {
          e.preventDefault();
          if (sectionId === 'inspiratie') {
            scrollToCasesSection();
          } else {
            scrollToSection(sectionId);
          }
          onClose();
        }}
        className="hover:text-terra-beige transition-colors py-3 border-b border-terra-beige/10"
        style={{ color: '#6D4F47' }}
      >
        {item.label}
      </a>
    );
  }
  
  // Regular links
  return (
    <Link 
      to={item.url}
      onClick={onClose}
      className="hover:text-terra-beige transition-colors py-3 border-b border-terra-beige/10"
      style={{ color: '#6D4F47' }}
    >
      {item.label}
    </Link>
  );
};

export const Header = () => {
  const { t: _t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [showNewsletter, setShowNewsletter] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [headerContent, setHeaderContent] = useState(null);

  // Fetch header content from API
  useEffect(() => {
    const fetchHeaderContent = async () => {
      try {
        const response = await axios.get(`${API}/header/content`);
        setHeaderContent(response.data);
      } catch (error) {
        logger.error('Failed to fetch header content:', error);
        // Use defaults on error
      }
    };
    fetchHeaderContent();
  }, []);

  // Get menu items from API or use defaults
  const menuItems = headerContent?.menu_items?.filter(item => item.is_active) || DEFAULT_MENU_ITEMS;
  const showB2B = headerContent?.show_b2b_link !== false;
  const b2bLabel = headerContent?.b2b_label || 'B2B Portal';

  // Helper function to handle section navigation
  const scrollToSection = (sectionId) => {
    const headerOffset = 100; // Header height
    
    const doScroll = () => {
      const section = document.getElementById(sectionId);
      if (section) {
        // Get the section's top position and subtract header offset
        const rect = section.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition = rect.top + scrollTop - headerOffset;
        
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      }
    };

    if (location.pathname !== '/') {
      // Navigate to homepage first, then scroll
      navigate('/');
      setTimeout(doScroll, 500);
    } else {
      // Already on homepage, just scroll
      doScroll();
    }
  };

  const scrollToCasesSection = () => {
    const headerOffset = 120;
    
    const doScroll = () => {
      const section = document.getElementById('inspiratie') || document.querySelector('section.cases-section');
      if (section) {
        const elementPosition = section.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    };

    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(doScroll, 300);
    } else {
      doScroll();
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setStatus('error');
      setMessage('Geldig e-mailadres vereist');
      return;
    }

    try {
      setStatus('loading');
      await axios.post(`${API}/newsletter/subscribe`, { email });
      
      setStatus('success');
      setMessage('Bedankt voor uw inschrijving!');
      setEmail('');
      
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
        setShowNewsletter(false);
      }, 3000);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Er ging iets mis.');
      
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 3000);
    }
  };

  return (
    <>
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
      {/* Niveau 1: Navigatie + Logo */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="hidden md:flex h-20 items-center justify-center">
          {/* Left Navigation - first half of items */}
          <nav className="flex items-center gap-8 font-sweet-sans text-sm uppercase tracking-wider">
            {menuItems.slice(0, Math.ceil(menuItems.length / 2)).map((item, index) => (
              <NavItem key={index} item={item} scrollToSection={scrollToSection} scrollToCasesSection={scrollToCasesSection} />
            ))}
          </nav>
          
          {/* Logo Center */}
          <div className="flex items-center mx-12">
            <Link to="/">
              <img 
                src="/logo-lart.png" 
                alt="L'ART Logo" 
                className="h-10 w-auto cursor-pointer hover:opacity-80 transition-opacity duration-200"
              />
            </Link>
          </div>
          
          {/* Right Navigation - second half of items */}
          <nav className="flex items-center gap-8 font-sweet-sans text-sm uppercase tracking-wider">
            {menuItems.slice(Math.ceil(menuItems.length / 2)).map((item, index) => (
              <NavItem key={index} item={item} scrollToSection={scrollToSection} scrollToCasesSection={scrollToCasesSection} />
            ))}
          </nav>
        </div>
      </div>
      
      {/* Niveau 2: Alle Iconen Centered - Only Desktop */}
      <div className="hidden md:block border-t border-border/20 bg-white/90">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-12 items-center justify-center gap-6">
            {/* B2B Portal Button */}
            {showB2B && (
              <div className="relative group">
                <Link 
                  to="/b2b/login" 
                  className="px-4 py-2 bg-earth-brown text-white font-sweet-sans text-xs hover:bg-earth-brown/90 transition-colors duration-200"
                >
                  {b2bLabel}
                </Link>
                <span className="absolute -bottom-10 left-1/2 -translate-x-1/2 bg-earth-brown text-white text-xs py-1 px-3 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none font-sweet z-50">
                  Toegang voor professionals
                </span>
              </div>
            )}
            
            {/* Shopping Cart */}
            <div className="relative group flex items-center">
              <CartDrawer />
              <span className="absolute -bottom-10 left-1/2 -translate-x-1/2 bg-earth-brown text-white text-xs py-1 px-3 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none font-sweet z-50">
                Winkelmandje
              </span>
            </div>
            
            {/* Language Switcher (Belgische vlag) */}
            <div className="relative group flex items-center">
              <LanguageSwitcher />
              <span className="absolute -bottom-10 left-1/2 -translate-x-1/2 bg-earth-brown text-white text-xs py-1 px-3 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none font-sweet z-50">
                Taal selecteren
              </span>
            </div>
            
            {/* Instagram */}
            <div className="relative group flex items-center">
              <a 
                href="https://instagram.com/l.art.be" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-earth-brown hover:text-terra-beige transition-colors flex items-center"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <span className="absolute -bottom-10 left-1/2 -translate-x-1/2 bg-earth-brown text-white text-xs py-1 px-3 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none font-sweet z-50">
                Volg ons op Instagram
              </span>
            </div>
            
            {/* Newsletter/Mail met notification badge */}
            <div className="relative group flex items-center">
              <button
                onClick={() => setShowNewsletter(!showNewsletter)}
                className="relative text-earth-brown hover:text-terra-beige transition-colors flex items-center"
              >
                <Mail className="w-4 h-4" />
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-terra-beige rounded-none animate-pulse"></span>
              </button>
              {!showNewsletter && (
                <span className="absolute -bottom-10 left-1/2 -translate-x-1/2 bg-earth-brown text-white text-xs py-1 px-3 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none font-sweet z-50">
                  Nieuwsbrief
                </span>
              )}
              
              {showNewsletter && (
                <>
                  {/* Backdrop */}
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setShowNewsletter(false)}
                  ></div>
                  
                  {/* Dropdown */}
                  <div className="absolute right-0 top-full mt-2 w-80 bg-white  shadow-2xl border-2 border-terra-beige/30 p-4 z-50 animate-in slide-in-from-top-2 duration-300">
                    {status === 'success' ? (
                      <div className="flex items-center gap-3 py-2">
                        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                        <p className="font-sweet-sans text-sm text-green-700">{message}</p>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="font-sweet-sans text-base uppercase tracking-wide font-normal text-earth-brown">
                              Blijf op de hoogte
                            </h4>
                            <p className="font-sweet-sans text-xs text-almost-black/60">
                              Ontvang nieuws & exclusieve acties
                            </p>
                          </div>
                          <button
                            onClick={() => setShowNewsletter(false)}
                            className="text-almost-black/40 hover:text-almost-black transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        
                        <form onSubmit={handleNewsletterSubmit} className="space-y-3">
                          <Input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="uw@email.be"
                            disabled={status === 'loading'}
                            className="w-full h-10 font-sweet"
                          />
                          
                          <Button
                            type="submit"
                            disabled={status === 'loading'}
                            className="w-full bg-earth-brown hover:bg-earth-brown/90 text-white font-sweet h-10"
                          >
                            {status === 'loading' ? (
                              <span className="flex items-center gap-2">
                                <Send className="w-4 h-4 animate-pulse" />
                                Verzenden...
                              </span>
                            ) : (
                              <span className="flex items-center gap-2">
                                <Send className="w-4 h-4" />
                                Inschrijven
                              </span>
                            )}
                          </Button>
                          
                          {status === 'error' && (
                            <p className="font-sweet-sans text-xs text-red-600">{message}</p>
                          )}
                        </form>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
            
            {/* Admin Login */}
            <div className="relative group flex items-center">
              <Link 
                to="/admin/login" 
                className="text-earth-brown hover:text-terra-beige transition-colors flex items-center"
              >
                <Lock className="w-4 h-4" />
              </Link>
              <span className="absolute -bottom-10 left-1/2 -translate-x-1/2 bg-earth-brown text-white text-xs py-1 px-3 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none font-sweet z-50">
                Admin Login
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile Header */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Mobile Header */}
        <div className="md:hidden flex h-20 items-center justify-between gap-4">
          <Link to="/">
            <img src="/logo-lart.png" alt="L'ART Logo" className="h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-2">
            <CartDrawer />
            
            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-earth-brown hover:text-terra-beige transition-colors p-2"
              aria-label="Toggle menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu Drawer */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 z-[100] md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          ></div>
          
          {/* Drawer */}
          <div className="fixed top-0 right-0 bottom-0 w-72 bg-white z-[101] shadow-2xl md:hidden overflow-hidden" style={{ height: '100vh' }}>
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-terra-beige/20 bg-white">
                <span className="font-angie text-2xl text-earth-brown">Menu</span>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-earth-brown hover:text-terra-beige p-2"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              {/* Navigation */}
              <nav className="flex-1 overflow-y-auto p-4 bg-white">
                <div className="flex flex-col gap-1 font-sweet-sans text-base uppercase tracking-wider">
                  {menuItems.map((item, index) => (
                    <MobileNavItem 
                      key={index} 
                      item={item} 
                      scrollToSection={scrollToSection} 
                      scrollToCasesSection={scrollToCasesSection}
                      onClose={() => setMobileMenuOpen(false)}
                    />
                  ))}
                  
                  {/* B2B Portal Link in Mobile Menu */}
                  {showB2B && (
                    <Link 
                      to="/b2b/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="bg-earth-brown text-white hover:bg-earth-brown/90 transition-colors py-3 px-4 text-center font-semibold mt-4"
                    >
                      {b2bLabel}
                    </Link>
                  )}
                </div>
              </nav>
              
              {/* Footer with social icons */}
              <div className="p-4 border-t border-terra-beige/20 bg-white">
                <div className="flex items-center justify-center gap-6">
                  <a 
                    href="https://instagram.com/l.art.be" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-earth-brown hover:text-terra-beige transition-colors"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                  <Link 
                    to="/admin/login" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-earth-brown hover:text-terra-beige transition-colors"
                  >
                    <Lock className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </header>
    </>
  );
};